import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import { forwardRef, useMemo } from 'react';
import styles from './range-price.module.scss.js';
import { clsx } from '../../external/clsx/dist/clsx.js';
import { FlexBox, ErrorBoundary, Typography } from '@vp/swan';
import { useLocalization } from '../../hooks/useLocalizations.js';
import { formatRangePrice } from '../../utils/formatter.util.js';
import { VatMessageInternal } from '../vat-message/vat-message.component.js';
import { usePriceLabels } from '../../utils/priceLabels.js';
import { SetupFeeInternal } from '../setup-fee/setup-fee.component.js';
import { getPrice, validatePriceValue } from '../../utils/price.util.js';

const RangePriceInternal = forwardRef(({ className, rangePriceConfig, placeholdertext, ...rest }, ref) => {
  const {
    locale,
    currency,
    shippingIncl,
    minimumFractionDigits,
    vatInc = false,
    lowestListPrice,
    highestListPrice,
    lowestDiscountedPrice,
    highestDiscountedPrice,
    hideShippingMessageInVatMessage,
    showSetupFeeInfo = true,
    isTile,
    setupFee,
    discountedSetupFee
  } = rangePriceConfig;
  const classNames = clsx(className, styles.rangePrice);
  const { t } = useLocalization(locale);
  const { OriginalPriceLabel, SalePriceLabel } = usePriceLabels(locale);
  const lowestListPriceValue = getPrice(lowestListPrice, vatInc);
  const highestListPriceValue = getPrice(highestListPrice, vatInc);
  const lowestDiscountedPriceValue = lowestDiscountedPrice ? getPrice(lowestDiscountedPrice, vatInc) : void 0;
  const highestDiscountedPriceValue = highestDiscountedPrice ? getPrice(highestDiscountedPrice, vatInc) : void 0;
  const isDiscountedRangePricePresent = lowestDiscountedPriceValue !== void 0 && highestDiscountedPriceValue !== void 0;
  if (isDiscountedRangePricePresent) {
    validatePriceValue(lowestDiscountedPriceValue, "lowestDiscountedPrice");
    validatePriceValue(highestDiscountedPriceValue, "highestDiscountedPrice");
  }
  validatePriceValue(lowestListPriceValue, "lowestListPrice");
  validatePriceValue(highestListPriceValue, "highestListPrice");
  const formattedRangeListPrice = useMemo(
    () => formatRangePrice(t, locale, currency, lowestListPriceValue, highestListPriceValue, minimumFractionDigits),
    [t, locale, currency, lowestListPriceValue, highestListPriceValue, minimumFractionDigits]
  );
  const formattedRangeDiscountedPrice = useMemo(
    () => isDiscountedRangePricePresent ? formatRangePrice(t, locale, currency, lowestDiscountedPriceValue, highestDiscountedPriceValue, minimumFractionDigits) : void 0,
    [t, locale, currency, lowestDiscountedPriceValue, highestDiscountedPriceValue, minimumFractionDigits, isDiscountedRangePricePresent]
  );
  const renderRangePrice = () => {
    const isDiscounted = isDiscountedRangePricePresent && (lowestListPriceValue !== lowestDiscountedPriceValue || highestListPriceValue !== highestDiscountedPriceValue);
    if (!isDiscounted) {
      return /* @__PURE__ */ jsx(Typography, { as: "span", ref, ...isTile ? { fontSkin: "body-small" } : { fontSize: "large" }, className: classNames, ...rest, children: t("unitRangePrice", {
        rangePrice: /* @__PURE__ */ jsx(Typography, { as: "span", fontSkin: isTile ? "body-small" : "title-subsection", children: formattedRangeListPrice })
      }) });
    }
    return /* @__PURE__ */ jsxs(Typography, { as: "span", ref, ...isTile ? { fontSkin: "body-small" } : { fontSize: "large" }, className: classNames, ...rest, children: [
      OriginalPriceLabel,
      /* @__PURE__ */ jsx("s", { children: formattedRangeListPrice }),
      /* @__PURE__ */ jsxs(Typography, { as: "span", pl: 2, children: [
        SalePriceLabel,
        t("unitRangePrice", {
          rangePrice: /* @__PURE__ */ jsx(Typography, { as: "span", textColor: "promo", fontSkin: isTile ? "body-small-bold" : "title-subsection", children: formattedRangeDiscountedPrice })
        })
      ] })
    ] });
  };
  const renderVatMessage = () => {
    return /* @__PURE__ */ jsx(
      VatMessageInternal,
      {
        locale,
        vatAndShippingConfig: { vatInc, shippingIncl, hideShippingMessageInVatMessage },
        placeholdertext,
        fontSkin: isTile ? "footnote" : "body-small"
      }
    );
  };
  const renderSetupFee = () => {
    const priceContext = {
      locale,
      currency,
      shippingIncl,
      minimumFractionDigits,
      vatInc
    };
    return /* @__PURE__ */ jsx(SetupFeeInternal, { placeholdertext, priceContext, setupFee, discountedSetupFee });
  };
  return /* @__PURE__ */ jsxs(FlexBox, { flexDirection: "column", className: classNames, ...rest, ref, children: [
    renderRangePrice(),
    isTile ? renderVatMessage() : showSetupFeeInfo ? setupFee ? /* @__PURE__ */ jsxs(Fragment, { children: [
      renderSetupFee(),
      renderVatMessage()
    ] }) : /* @__PURE__ */ jsxs(Fragment, { children: [
      renderVatMessage(),
      renderSetupFee()
    ] }) : renderVatMessage()
  ] });
});
const RangePrice = forwardRef((props, ref) => {
  return /* @__PURE__ */ jsx(ErrorBoundary, { fallback: /* @__PURE__ */ jsx(Typography, { as: "span", children: props.placeholdertext }), children: /* @__PURE__ */ jsx(RangePriceInternal, { ...props, ref }) });
});

export { RangePrice };
